/**
 * AvatarFlow — state-machine orchestrator for all 6 LIVE avatar SKUs.
 * Replaces services/astro-bot-miniapp/src/screens/AvatarFlow.tsx.
 *
 * Contract: specs/023-avatar-ui-fidelity/contracts/AvatarFlow.contract.ts
 * State machine: data-model.md §1.2.
 *
 * Preserves sessionStorage[avatarFlow.r2Key.<code>] behavior verbatim.
 */

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WebApp from '@twa-dev/sdk';
import { useSku } from '../../hooks/useSku';
import { useSkuInvoice } from '../../hooks/useSkuInvoice';
import { useAvatarPack } from '../../hooks/useAvatarPack';
import { useMe } from '../../hooks/useMe';
import { useAuth } from '../../store/auth';
import { discoverSkuRunIds, getBaselineSkuRunId } from '../../lib/discover-sku-run';
import { getSkuFlow } from '../../lib/sku-flow-registry';
import type { AvatarFlowState, AvatarFlowError, AvatarResult, AvatarResultGridVariant } from '../../types/avatarFlow';
import { SelfieConsentModal, hasCurrentBiometricConsent } from './SelfieConsentModal';
import { SelfieUploadScreen } from './SelfieUploadScreen';
import { GenerationLoadingScreen } from './GenerationLoadingScreen';
import { PaymentSheet } from '../payment/PaymentSheet';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { RevealEntrance } from './RevealEntrance';
import { ResultGrid } from './ResultGrid/ResultGrid';
// Lazy: react-zoom-pan-pinch lives in SingleFullscreen — load it only when a
// frame is opened fullscreen, keeping the zoom lib out of the initial bundle.
const SingleFullscreen = lazy(() =>
  import('./ResultGrid/SingleFullscreen').then((m) => ({ default: m.SingleFullscreen })),
);
import { HdScenePicker } from './HdScenePicker';
import { ZodiacProgress12 } from './ZodiacProgress12';
import { exampleUrl, getExamplesFolderForSku } from '../../lib/sku-examples';

const r2KeyStorageKey = (code: string): string => `avatarFlow.r2Key.${code}`;

function readPersistedR2Key(code: string): string | null {
  try {
    return sessionStorage.getItem(r2KeyStorageKey(code));
  } catch {
    return null;
  }
}

function clearPersistedR2Key(code: string): void {
  try {
    sessionStorage.removeItem(r2KeyStorageKey(code));
  } catch {
    /* storage unavailable */
  }
}

function buildResultFromPackFrames(
  skuCode: string,
  variant: AvatarResultGridVariant,
  frames: Array<{ id: string; imageUrl: string; thumbnailUrl: string }>,
): AvatarResult {
  return {
    skuCode,
    variant,
    frames: frames.map((f, i) => ({
      id: f.id || `${skuCode}_${i}`,
      imageUrl: f.imageUrl,
      thumbnailUrl: f.thumbnailUrl,
    })),
  };
}

function wireBackButton(handler: () => void): () => void {
  try {
    const bb = WebApp?.BackButton;
    if (!bb || typeof bb.onClick !== 'function') return () => {};
    bb.onClick(handler);
    bb.show();
    return () => {
      try {
        bb.offClick(handler);
        bb.hide();
      } catch {
        /* noop */
      }
    };
  } catch {
    return () => {};
  }
}

export function AvatarFlow(): JSX.Element {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['flows', 'common']);
  const { sku, isLoading, isError } = useSku(code);
  const { purchase, redeemCoupon } = useSkuInvoice<{ selfieR2Key: string }>();
  const jwt = useAuth((s) => s.jwt);
  const [activePackId, setActivePackId] = useState<string | null>(null);
  const { snapshot: packSnapshot, error: packError } = useAvatarPack(activePackId);
  const { data: me, isLoading: meLoading } = useMe();
  const flowEntry = getSkuFlow(code);
  const variant: AvatarResultGridVariant =
    (flowEntry.kind === 'live' && flowEntry.resultGridVariant) || 'grid-3x3';
  const meta = SKU_CATALOG[code] ?? null;
  const totalFrames = meta?.frameCount;
  const paidStars = sku?.priceStars ?? undefined;

  // Async-aware initial state: start with a sentinel while me is loading to avoid flicker.
  // Once me resolves we settle to 'consent' or 'upload' via the useEffect below.
  const [state, setState] = useState<AvatarFlowState>(() => {
    const persisted = readPersistedR2Key(code);
    if (persisted) return { name: 'invoice', selfieR2Key: persisted };
    // Default to loading-consent until me loads; effect below resolves it.
    return { name: 'consent' };
  });

  // Settle consent vs upload once me data is available (no flicker: hidden behind loading guard)
  useEffect(() => {
    if (meLoading) return;
    // Only settle if we are still in the initial 'consent' state (no persisted key, no upload yet)
    setState(prev => {
      if (prev.name !== 'consent') return prev;
      if (hasCurrentBiometricConsent(me)) return { name: 'upload' };
      return { name: 'consent' };
    });
  }, [me, meLoading]);

  const stateRef = useRef(state);
  stateRef.current = state;

  const goBack = useCallback(() => {
    const cur = stateRef.current;
    switch (cur.name) {
      case 'fullscreen':
        setState({ name: 'grid', result: cur.result });
        break;
      case 'grid':
      case 'reveal-entrance':
      case 'error':
        navigate('/catalog');
        break;
      case 'invoice':
      case 'loading':
      case 'paying':
      case 'upload':
        clearPersistedR2Key(code);
        navigate('/catalog');
        break;
      case 'consent':
        navigate('/catalog');
        break;
      default:
        navigate('/catalog');
    }
  }, [code, navigate]);

  useEffect(() => wireBackButton(goBack), [goBack]);

  const handleConsentAccept = useCallback(() => setState({ name: 'upload' }), []);
  const handleConsentDecline = useCallback(() => navigate('/catalog'), [navigate]);

  const handleUploaded = useCallback((r2Key: string) => {
    setState({ name: 'invoice', selfieR2Key: r2Key });
  }, []);

  const handleInvoicePay = useCallback((r2Key: string) => {
    setState({ name: 'paying', selfieR2Key: r2Key });
  }, []);

  const handleInvoiceCancel = useCallback(() => {
    clearPersistedR2Key(code);
    navigate('/catalog');
  }, [code, navigate]);

  // Coupon path: redeem a 100%-free code instead of paying with Stars. Rejects
  // (so the invoice sheet shows an inline error) when the code is invalid; on a
  // successful redeem we transition straight into the generation loading state.
  const handleApplyCoupon = useCallback(
    async (r2Key: string, couponCode: string) => {
      const baselineRunId = jwt ? await getBaselineSkuRunId(jwt, code) : null;
      const result = await redeemCoupon(code, couponCode, { selfieR2Key: r2Key });
      let firstPackId: string | null = result.packIds[0] ?? null;
      if (!firstPackId && jwt) {
        const ids = await discoverSkuRunIds(jwt, code, baselineRunId);
        firstPackId = ids?.packIds[0] ?? null;
      }
      if (!firstPackId) {
        setState({
          name: 'error',
          reason: { kind: 'generation-failed', messageKey: 'avatarFlow.error.generation' },
        });
        return;
      }
      clearPersistedR2Key(code);
      setActivePackId(firstPackId);
      setState({ name: 'loading', selfieR2Key: r2Key, startedAt: Date.now() });
    },
    [code, jwt, redeemCoupon],
  );

  // Auto-pay when entering `paying` state.
  useEffect(() => {
    if (state.name !== 'paying') return;
    const r2Key = state.selfieR2Key;
    let cancelled = false;
    (async () => {
      try {
        // Capture the pre-purchase baseline run id so real-Stars discovery can
        // identify the freshly created pack without relying on clock agreement.
        const baselineRunId = jwt ? await getBaselineSkuRunId(jwt, code) : null;
        if (cancelled) return;
        const result = await purchase(code, { selfieR2Key: r2Key });
        if (cancelled) return;
        const status = result.status;
        if (status === 'paid') {
          // Mock-pay resolves packIds directly. Real Stars dispatches the job
          // asynchronously in the successful_payment webhook, so the resolved
          // packIds are empty — discover the freshly created pack via /sku-runs.
          let firstPackId: string | null = result.packIds[0] ?? null;
          if (!firstPackId && jwt) {
            const ids = await discoverSkuRunIds(jwt, code, baselineRunId);
            if (cancelled) return;
            firstPackId = ids?.packIds[0] ?? null;
          }
          if (!firstPackId) {
            setState({
              name: 'error',
              reason: { kind: 'generation-failed', messageKey: 'avatarFlow.error.generation' },
            });
            return;
          }
          clearPersistedR2Key(code);
          setActivePackId(firstPackId);
          setState({ name: 'loading', selfieR2Key: r2Key, startedAt: Date.now() });
        } else if (status === 'cancelled') {
          setState({
            name: 'error',
            reason: { kind: 'payment-cancelled', messageKey: 'avatarFlow.error.paymentCancelled' },
          });
        } else {
          setState({
            name: 'error',
            reason: { kind: 'payment-failed', messageKey: 'avatarFlow.error.payment' },
          });
        }
      } catch {
        if (cancelled) return;
        setState({
          name: 'error',
          reason: { kind: 'payment-failed', messageKey: 'avatarFlow.error.payment' },
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, code, purchase, jwt]);

  // Real generation: flip to reveal once the polled AvatarPack is COMPLETED.
  useEffect(() => {
    if (state.name !== 'loading') return;
    if (!packSnapshot) return;
    if (packSnapshot.status === 'COMPLETED' && packSnapshot.frames.length > 0) {
      setState({
        name: 'reveal-entrance',
        result: buildResultFromPackFrames(code, variant, packSnapshot.frames),
      });
    } else if (packSnapshot.status === 'FAILED') {
      const raw = packSnapshot.error ?? '';
      if (/daily cap of \d+ generations/i.test(raw)) {
        setState({
          name: 'error',
          reason: { kind: 'daily-cap', messageKey: 'avatarFlow.error.dailyCap', detail: raw },
        });
      } else if (/global daily cap/i.test(raw)) {
        setState({
          name: 'error',
          reason: { kind: 'global-cap', messageKey: 'avatarFlow.error.globalCap', detail: raw },
        });
      } else {
        setState({
          name: 'error',
          reason: { kind: 'generation-failed', messageKey: 'avatarFlow.error.generation', detail: raw },
        });
      }
    }
  }, [state.name, packSnapshot, code, variant]);

  useEffect(() => {
    if (!packError) return;
    if (state.name !== 'loading') return;
    setState({
      name: 'error',
      reason: { kind: 'network', messageKey: 'avatarFlow.error.network' },
    });
  }, [packError, state.name]);

  if (isLoading || meLoading) return <div data-testid="avatar-flow-loading" />;
  if (isError || !sku) {
    return (
      <div data-testid="avatar-flow-error">
        <p>{t('avatarFlow.error.couldntLoad', { ns: 'flows' })}</p>
        <button type="button" onClick={() => void navigate('/catalog')}>
          {t('avatarFlow.backToCatalog', { ns: 'flows' })}
        </button>
      </div>
    );
  }

  switch (state.name) {
    case 'detail':
      // Detail is rendered by /sku/:code (SkuDetail screen). Falling here only
      // if directly deep-linked into the flow without consent path; rare.
      return <SelfieConsentModal open jwt={jwt} onAccept={handleConsentAccept} onDecline={handleConsentDecline} />;

    case 'consent':
      return <SelfieConsentModal open jwt={jwt} onAccept={handleConsentAccept} onDecline={handleConsentDecline} />;

    case 'upload':
      return <SelfieUploadScreen skuCode={code} onUploaded={handleUploaded} />;

    case 'invoice': {
      const skuTitle = t(sku.nameKey, { defaultValue: code });
      const skuSubtitle = t(sku.descKey, { defaultValue: '' });
      const rawBullets = t('common:sku.' + code + '.bullets', { returnObjects: true, defaultValue: [] });
      const skuBullets = Array.isArray(rawBullets) ? (rawBullets as string[]) : [];
      const priceStars = sku.priceStars ?? 0;
      const folder = getExamplesFolderForSku(code);
      const thumbUrl = folder ? exampleUrl(folder, 1) : undefined;
      return (
        <PaymentSheet
          skuCode={code}
          title={skuTitle}
          subtitle={skuSubtitle || undefined}
          priceStars={priceStars}
          bullets={skuBullets}
          thumbUrl={thumbUrl}
          onPay={() => handleInvoicePay(state.selfieR2Key)}
          onCancel={handleInvoiceCancel}
          onApplyCoupon={(couponCode) => handleApplyCoupon(state.selfieR2Key, couponCode)}
        />
      );
    }

    case 'paying':
      return (
        <GenerationLoadingScreen
          totalFrames={totalFrames}
          framesReady={0}
          paidStars={paidStars ?? undefined}
        />
      );

    case 'loading':
      return (
        <GenerationLoadingScreen
          totalFrames={totalFrames}
          framesReady={packSnapshot?.frames.length ?? 0}
          paidStars={paidStars ?? undefined}
          phase={packSnapshot?.phase ?? null}
        />
      );

    case 'reveal-entrance':
      return (
        <RevealEntrance
          frames={state.result.frames}
          totalFrames={totalFrames ?? state.result.frames.length}
          onComplete={() => setState({ name: 'grid', result: state.result })}
        />
      );

    case 'grid': {
      const crossSellTarget = code === 'avatar_max' ? 'zodiac_collection' : 'avatar_max';
      return (
        <ResultGrid
          result={state.result}
          onSelectFrame={(index) =>
            setState({ name: 'fullscreen', result: state.result, frameIndex: index })
          }
          onCrossSell={() => navigate(`/sku/${crossSellTarget}`)}
          onBack={() => navigate('/catalog')}
          testId="result-grid"
        />
      );
    }

    case 'fullscreen': {
      const total = state.result.frames.length;
      const cur = state.frameIndex;
      const goTo = (next: number) =>
        setState({ name: 'fullscreen', result: state.result, frameIndex: (next + total) % total });
      return (
        <Suspense fallback={null}>
          <SingleFullscreen
            frame={state.result.frames[cur] ?? state.result.frames[0]!}
            frameIndex={cur + 1}
            frameTotal={total}
            onClose={() => setState({ name: 'grid', result: state.result })}
            onPrev={total > 1 ? () => goTo(cur - 1) : undefined}
            onNext={total > 1 ? () => goTo(cur + 1) : undefined}
          />
        </Suspense>
      );
    }

    case 'hd-scene-pick':
      return (
        <HdScenePicker
          scenes={state.scenes}
          onPick={() => {
            // Re-enter paying for the HD render of the picked scene.
            const r2Key = readPersistedR2Key(code) ?? '';
            setState({ name: 'paying', selfieR2Key: r2Key });
          }}
        />
      );

    case 'zodiac-progress':
      return <ZodiacProgress12 signsDone={state.signsDone} />;

    case 'error': {
      const reason = state.reason;
      const titleByKind: Record<AvatarFlowError['kind'], string> = {
        'upload-failed': t('avatarFlow.error.title.upload', { defaultValue: 'The portal refused your offering' }),
        'payment-failed': t('avatarFlow.error.title.payment', { defaultValue: 'The stars did not accept the payment' }),
        'payment-cancelled': t('avatarFlow.error.title.paymentCancelled', { defaultValue: 'The magic was interrupted' }),
        'generation-failed': t('avatarFlow.error.title.generation', { defaultValue: 'The Akashic chronicles fell silent' }),
        'daily-cap': t('avatarFlow.error.title.dailyCap', { defaultValue: 'The gates are sealed for today' }),
        'global-cap': t('avatarFlow.error.title.globalCap', { defaultValue: 'The cosmos needs to rest' }),
        'network': t('avatarFlow.error.title.network', { defaultValue: 'The thread to the cosmos was severed' }),
      };
      const bodyByKind: Record<AvatarFlowError['kind'], string> = {
        'upload-failed': t('avatarFlow.error.body.upload', { defaultValue: 'Your portrait could not be carried across the threshold. Try another selfie.' }),
        'payment-failed': t('avatarFlow.error.body.payment', { defaultValue: 'Your Stars never reached the keeper of the gates. No charge was taken — try again in a moment.' }),
        'payment-cancelled': t('avatarFlow.error.body.paymentCancelled', { defaultValue: 'You stepped back before it began. Nothing was taken.' }),
        'generation-failed': t('avatarFlow.error.body.generation', { defaultValue: 'The weaving frayed before completion. Your Stars will be returned automatically — try again shortly.' }),
        'daily-cap': t('avatarFlow.error.body.dailyCap', { defaultValue: 'The gates of time only open a few times each cycle. Return tomorrow — the chronicles will be ready.' }),
        'global-cap': t('avatarFlow.error.body.globalCap', { defaultValue: 'Many seekers passed through today and the cosmos must replenish. Return in a few hours.' }),
        'network': t('avatarFlow.error.body.network', { defaultValue: 'The signal to the cosmos faltered. Check your connection and try again.' }),
      };
      return (
        <div data-testid="avatar-flow-error-state" role="alert" className="avatarFlow-error">
          <h2>{titleByKind[reason.kind]}</h2>
          <p>{bodyByKind[reason.kind]}</p>
          {reason.detail && import.meta.env.DEV ? (
            <pre style={{ opacity: 0.5, fontSize: 11, marginTop: 12, whiteSpace: 'pre-wrap' }}>
              {reason.detail}
            </pre>
          ) : null}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            {reason.kind !== 'daily-cap' && reason.kind !== 'global-cap' ? (
              <button
                type="button"
                onClick={() => {
                  const persisted = readPersistedR2Key(code);
                  if (persisted) setState({ name: 'invoice', selfieR2Key: persisted });
                  else setState({ name: 'upload' });
                }}
              >
                {t('avatarFlow.error.retry', { defaultValue: 'Try again' })}
              </button>
            ) : null}
            <button type="button" onClick={() => navigate('/catalog')}>
              {t('avatarFlow.error.back', { defaultValue: 'Back to catalog' })}
            </button>
          </div>
        </div>
      );
    }

    default:
      return <div />;
  }
}

export default AvatarFlow;
